import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import {
  ShoppingCart,
  Video,
  Clock,
  Youtube,
  Check,
  Monitor,
  Download,
  Key,
  QrCode,
  X,
  Upload,
  CheckCircle2,
  Copy,
  Sparkles,
  User,
  Mail,
  Phone,
  CreditCard,
  Image as ImageIcon
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";

import { products, Product } from "@/data/products";
import { createCartOrdersWithProof } from "@/lib/license-service";

const AIProgram = () => {
  const { toast } = useToast();
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { cart, addToCart: cartAddToCart, removeFromCart, clearCart, totalPrice, showCart, setShowCart } = useCart();
  const [showPayment, setShowPayment] = useState(false);
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [slipUrl, setSlipUrl] = useState<string | null>(null);
  const [orderIds, setOrderIds] = useState<string[]>([]);

  const addToCart = (product: Product) => {
    const added = cartAddToCart(product);
    if (!added) {
      toast({
        title: t("มีในตะกร้าแล้ว", "Already in cart"),
        description: t(`${product.name} อยู่ในตะกร้าของคุณแล้ว`, `${product.name} is already in your cart`),
      });
      return;
    }
    toast({
      title: t("เพิ่มลงตะกร้าแล้ว", "Added to cart"),
      description: t(`${product.name} ถูกเพิ่มลงตะกร้า`, `${product.name} has been added to cart`),
    });
  };

  const handlePayment = () => {
    if (!user) {
      toast({
        variant: "destructive",
        title: t("กรุณาเข้าสู่ระบบ", "Please login"),
        description: t("คุณต้องเข้าสู่ระบบก่อนดำเนินการชำระเงิน", "You must login before proceeding to checkout")
      });
      setShowCart(false);
      navigate("/login");
      return;
    }
    setShowCart(false);
    setShowPayment(true);
    setIsUploaded(false);
    setUploadedFile(null);
    setSlipUrl(null);
  };

  const handleConfirmPayment = async () => {
    if (!isUploaded || !slipUrl) {
      toast({
        variant: "destructive",
        title: t("กรุณาอัพโหลดสลิป", "Please upload slip"),
        description: t("คุณต้องอัพโหลดสลิปโอนเงินก่อนยืนยันการชำระเงิน", "You must upload the slip before confirming payment")
      });
      return;
    }

    if (!user) return; // Should not happen due to handlePayment check

    try {
      const cartItems = cart.map(item => ({
        name: item.name,
        price: item.price
      }));

      const result = await createCartOrdersWithProof(user.id, cartItems, slipUrl);

      if (!result.orderIds?.length) {
        toast({
          variant: "destructive",
          title: t("เกิดข้อผิดพลาด", "Error"),
          description: result.error || t("ไม่สามารถสร้างคำสั่งซื้อได้", "Could not create orders")
        });
        return;
      }

      setOrderIds(result.orderIds);
      setPaymentSubmitted(true);
      clearCart();
      setIsUploaded(false);
      setUploadedFile(null);
      setSlipUrl(null);

      if (!result.success && result.error) {
        toast({
          variant: "destructive",
          title: t("แจ้งโอนบางรายการไม่สำเร็จ", "Some items failed"),
          description: result.error
        });
      }

      toast({
        title: t("แจ้งโอนสำเร็จ!", "Payment submitted!"),
        description: t("ทีมงานจะตรวจสอบและอนุมัติคำสั่งซื้อให้คุณ", "Our team will review and approve your order"),
      });
    } catch (error) {
      console.error('Payment error:', error);
      toast({
        variant: "destructive",
        title: t("เกิดข้อผิดพลาด", "Error"),
        description: t("เกิดข้อผิดพลาดในการดำเนินการ", "An error occurred during processing")
      });
    }
  };

  const copyToClipboard = (text: string, clean: boolean = false) => {
    let finalContent = text;
    if (clean) finalContent = text.replace(/-/g, "");

    navigator.clipboard.writeText(finalContent);
    toast({
      title: t("คัดลอกแล้ว", "Copied"),
      description: t("คัดลอกไปยัง Clipboard เรียบร้อย", "Copied to clipboard"),
    });
  };

  const handleSaveQR = () => {
    const link = document.createElement("a");
    link.href = "/kbank-bas.jpg";
    link.download = "payment-qr.jpg";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast({
      title: t("บันทึกรูปภาพแล้ว", "QR Saved"),
      description: t("รูปภาพถูกบันทึกไปยังเครื่องของคุณแล้ว", "QR code has been saved to your device"),
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          variant: "destructive",
          title: t("ไฟล์ไม่ถูกต้อง", "Invalid file"),
          description: t("กรุณาอัพโหลดไฟล์รูปภาพเท่านั้น", "Please upload image files only")
        });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast({
          variant: "destructive",
          title: t("ไฟล์ใหญ่เกินไป", "File too large"),
          description: t("ขนาดไฟล์ต้องไม่เกิน 5MB", "File size must not exceed 5MB")
        });
        return;
      }

      setUploading(true);
      (async () => {
        try {
          const fileExt = file.name.split('.').pop() || 'jpg';
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
          const filePath = `ai-program/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('payment-slips')
            .upload(filePath, file);

          if (uploadError) throw uploadError;

          const { data: publicUrlData } = supabase.storage
            .from('payment-slips')
            .getPublicUrl(filePath);

          if (!publicUrlData?.publicUrl) {
            throw new Error("Failed to get slip URL");
          }

          setUploadedFile(file);
          setSlipUrl(publicUrlData.publicUrl);
          setIsUploaded(true);
          toast({
            title: t("อัพโหลดสำเร็จ", "Upload Successful"),
            description: t(`อัพโหลด ${file.name} เรียบร้อยแล้ว`, `Successfully uploaded ${file.name}`)
          });
        } catch (error: any) {
          console.error("Slip upload error:", error);
          setUploadedFile(null);
          setSlipUrl(null);
          setIsUploaded(false);
          toast({
            variant: "destructive",
            title: t("อัพโหลดล้มเหลว", "Upload failed"),
            description: error?.message || t("ไม่สามารถอัพโหลดไฟล์ได้ กรุณาลองใหม่อีกครั้ง", "Could not upload file. Please try again")
          });
        } finally {
          setUploading(false);
        }
      })();
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">
              <Download className="h-3 w-3 mr-1" />
              AI Programs
            </Badge>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
              <span className="text-gradient">AI Program</span> {t("สำหรับ Creator", "for Creators")}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t("เครื่องมือ AI ช่วยงาน Video และ Content สำหรับมืออาชีพ", "AI tools for Video and Content professionals")}
            </p>
          </div>

          {/* Cart Button */}
          <div className="fixed top-20 right-4 z-40">
            <Button
              variant="glow"
              size="lg"
              className="gap-2 shadow-lg"
              onClick={() => setShowCart(true)}
            >
              <ShoppingCart className="h-5 w-5" />
              {t("ตะกร้า", "Cart")} ({cart.length})
              {cart.length > 0 && (
                <Badge variant="secondary" className="ml-1">
                  ฿{totalPrice.toLocaleString()}
                </Badge>
              )}
            </Button>
          </div>

          {/* Products Grid */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {products.map((product) => (
              <Card
                key={product.id}
                variant={product.popular ? "glow" : "glass"}
                className="relative overflow-hidden"
              >
                {product.popular && (
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-primary text-primary-foreground">
                      {t("ยอดนิยม", "Popular")}
                    </Badge>
                  </div>
                )}

                <CardHeader className="text-center pb-2">
                  <div className="mx-auto p-4 rounded-2xl bg-primary/10 text-primary mb-4 w-fit">
                    {product.icon}
                  </div>
                  <CardTitle className="text-2xl">{product.name}</CardTitle>
                  <CardDescription className="text-base">
                    {t(product.descriptionTh, product.descriptionEn)}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <div className="text-center mb-6">
                    <div className="text-4xl font-bold text-gradient">
                      ฿{product.price.toLocaleString()}
                    </div>
                    <div className="flex items-center justify-center gap-2 text-muted-foreground mt-2">
                      <Monitor className="h-4 w-4" />
                      <span>{t(`ใช้ได้ ${product.devices} เครื่อง`, `${product.devices} devices`)}</span>
                    </div>
                  </div>

                  <ul className="space-y-3">
                    {(t(product.featuresTh.join("|||"), product.featuresEn.join("|||"))).split("|||").map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter className="flex flex-col gap-3">
                  <Link to={`/ai-program/${product.id}`} className="w-full">
                    <Button
                      variant="ghost"
                      className="w-full gap-2 border border-primary/20 hover:bg-primary/10"
                    >
                      <Sparkles className="h-4 w-4" />
                      {t("ดูรายละเอียด", "View Details")}
                    </Button>
                  </Link>
                  <Button
                    variant={product.popular ? "glow" : "outline"}
                    className="w-full gap-2"
                    onClick={() => addToCart(product)}
                  >
                    <ShoppingCart className="h-4 w-4" />
                    {t("เพิ่มลงตะกร้า", "Add to Cart")}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Features Section */}
          <div className="bg-muted/30 rounded-3xl p-8 md:p-12">
            <h2 className="text-2xl font-bold text-center mb-8">{t("ทำไมต้องเลือกเรา?", "Why Choose Us?")}</h2>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { icon: <Download className="h-6 w-6" />, titleTh: "ดาวน์โหลดทันที", titleEn: "Instant Download", descTh: "รับ Link ทันทีหลังชำระ", descEn: "Get link immediately after payment" },
                { icon: <Key className="h-6 w-6" />, titleTh: "License Key", titleEn: "License Key", descTh: "ใช้งานได้ 2 เครื่อง", descEn: "Use on 2 devices" },
                { icon: <CheckCircle2 className="h-6 w-6" />, titleTh: "อัพเดทฟรี", titleEn: "Free Updates", descTh: "รับอัพเดทฟรี 1 ปี", descEn: "Free updates for 1 year" },
                { icon: <Monitor className="h-6 w-6" />, titleTh: "ซัพพอร์ต", titleEn: "Support", descTh: "ช่วยเหลือตลอด 24 ชม.", descEn: "24/7 support" },
              ].map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="mx-auto p-3 rounded-xl bg-primary/10 text-primary w-fit mb-3">
                    {item.icon}
                  </div>
                  <h3 className="font-semibold mb-1">{t(item.titleTh, item.titleEn)}</h3>
                  <p className="text-sm text-muted-foreground">{t(item.descTh, item.descEn)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      {/* Cart Sidebar */}
      {showCart && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" onClick={() => setShowCart(false)}>
          <div
            className="absolute right-0 top-0 h-full w-full max-w-md bg-background border-l border-border p-6 overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">{t("ตะกร้าสินค้า", "Shopping Cart")}</h2>
              <Button variant="ghost" size="icon" onClick={() => setShowCart(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            {cart.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <ShoppingCart className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>{t("ตะกร้าว่างเปล่า", "Cart is empty")}</p>
              </div>
            ) : (
              <>
                <div className="space-y-4 mb-6">
                  {cart.map((item) => (
                    <Card key={item.id} variant="glass">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="p-2 rounded-lg bg-primary/10 text-primary">
                              {item.icon}
                            </div>
                            <div>
                              <h3 className="font-semibold">{item.name}</h3>
                              <p className="text-sm text-muted-foreground">
                                {t(`ใช้ได้ ${item.devices} เครื่อง`, `${item.devices} devices`)}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeFromCart(item.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="text-right mt-2">
                          <span className="font-bold text-primary">
                            ฿{item.price.toLocaleString()}
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg">{t("ยอดรวม", "Total")}</span>
                    <span className="text-2xl font-bold text-gradient">
                      ฿{totalPrice.toLocaleString()}
                    </span>
                  </div>
                  <Button variant="glow" className="w-full gap-2" onClick={handlePayment}>
                    <QrCode className="h-4 w-4" />
                    {t("ชำระเงิน", "Checkout")}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPayment && !paymentSubmitted && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xl flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-300">
          <div className="w-full max-w-xl my-auto animate-in zoom-in-95 duration-300">
            <Card className="w-full border-white/20 shadow-2xl overflow-hidden bg-background/90 backdrop-blur-2xl ring-1 ring-white/20">
              <CardHeader className="border-b border-border/50 bg-gradient-to-r from-primary/10 via-transparent to-transparent pb-6 pt-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-2xl bg-primary/20 text-primary shadow-inner">
                      <QrCode className="h-6 w-6" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl font-bold tracking-tight">{t("ชำระเงิน", "Payment")}</CardTitle>
                      <p className="text-xs text-muted-foreground mt-0.5">{t("กรุณาชำระเงินเพื่อเปิดใช้งานโปรแกรม", "Please complete payment to activate program")}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="rounded-full hover:bg-destructive/10 hover:text-destructive transition-colors" onClick={() => setShowPayment(false)}>
                    <X className="h-5 w-5" />
                  </Button>
                </div>
              </CardHeader>

              <CardContent className="p-0">
                <div className="p-6 md:p-8 space-y-8">
                  {/* Info helper */}
                  <div className="flex items-center gap-2 px-2 py-2 rounded-lg bg-primary/5 border border-primary/10 mb-6">
                    <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
                    <p className="text-[11px] font-medium text-primary/80">
                      {t("ขอบคุณที่ไว้วางใจเลือกใช้บริการของเรา", "Thank you for choosing our service.")}
                    </p>
                  </div>

                  {/* Order Summary & Bank Details Grouped */}
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Left side: QR & Summary */}
                    <div className="space-y-6">
                      <div className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center">
                        <div className="bg-white p-3 rounded-xl inline-block shadow-xl mb-3">
                          <img
                            src="/kbank-bas.jpg"
                            alt="KBANK QR Code"
                            className="w-32 h-32 md:w-40 md:h-40 object-contain"
                          />
                        </div>
                        <p className="text-xs font-bold text-primary animate-pulse mb-2">
                          {t("สแกน QR Code เพื่อชำระเงิน", "Scan QR Code to pay")}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 text-[11px] gap-2 rounded-lg border-primary/20 hover:bg-primary/10 hover:text-primary transition-all active:scale-95"
                          onClick={handleSaveQR}
                        >
                          <Download className="h-3.5 w-3.5" />
                          {t("บันทึกภาพ QR Code", "Save QR Image")}
                        </Button>
                      </div>

                      <div className="bg-primary/5 rounded-2xl p-4 border border-primary/20">
                        <div className="flex justify-between items-end">
                          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{t("ยอดรวมสุทธิ", "Total Amount")}</span>
                          <span className="text-3xl font-black text-primary tracking-tighter">฿{totalPrice.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Right side: Bank Details */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 mb-1">
                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-widest">{t("ข้อมูลบัญชี", "Bank Details")}</h3>
                      </div>

                      <div className="space-y-3">
                        <div className="group/bank p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-primary/40 transition-all">
                          <span className="text-[10px] text-muted-foreground font-bold uppercase block mb-1">{t("ธนาคาร", "Bank")}</span>
                          <div className="flex items-center justify-between">
                            <span className="font-bold text-sm">KBANK กสิกรไทย</span>
                            <div className="h-2 w-2 rounded-full bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
                          </div>
                        </div>

                        <div className="group/bank p-4 rounded-2xl bg-white/10 border border-primary/30 shadow-lg shadow-primary/5 relative overflow-hidden">
                          <div className="absolute top-0 right-0 p-2 opacity-10">
                            <Copy className="h-12 w-12" />
                          </div>
                          <span className="text-[10px] text-primary font-black uppercase block mb-1">{t("เลขบัญชี", "Account No.")}</span>
                          <div className="flex items-center justify-between gap-2 relative z-10">
                            <span className="font-mono font-black text-lg tracking-wider text-gradient">457-2-10138-5</span>
                            <Button
                              variant="glow"
                              size="icon"
                              className="h-8 w-8 rounded-lg shadow-lg"
                              onClick={() => copyToClipboard("457-2-10138-5", true)}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        <div className="group/bank p-4 rounded-2xl bg-white/5 border border-white/10">
                          <span className="text-[10px] text-muted-foreground font-bold uppercase block mb-1">{t("ชื่อบัญชี", "Account Name")}</span>
                          <span className="font-bold text-sm">รณภพ ชนะพิมพ์</span>
                        </div>
                      </div>

                      <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                        <div className="flex gap-2">
                          <Clock className="h-4 w-4 text-yellow-500 shrink-0 mt-0.5" />
                          <p className="text-[10px] leading-relaxed text-black font-semibold">
                            {t("โอนจากทุกแบงค์อนุมัติทันที ส่วน BBL รอ 10 นาที", "Instant approval for all banks except BBL (10 min wait)")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Upload Slip with special emphasis */}
                  <div
                    className={`group/upload relative overflow-hidden border-2 border-dashed rounded-2xl p-6 md:p-8 text-center transition-all cursor-pointer ${isUploaded
                      ? "border-green-500/50 bg-green-500/10 shadow-[inner_0_0_20px_rgba(34,197,94,0.1)]"
                      : uploading
                        ? "border-primary/40 bg-primary/5"
                        : "border-border hover:border-primary/50 hover:bg-white/5"
                      }`}
                  >
                    <input
                      type="file"
                      id="slip-upload"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                    <label htmlFor="slip-upload" className="cursor-pointer block">
                      {isUploaded ? (
                        <div className="flex flex-col items-center py-2 animate-in fade-in zoom-in duration-300">
                          <div className="h-16 w-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                            <CheckCircle2 className="h-8 w-8 text-green-500" />
                          </div>
                          <p className="text-xl font-black text-green-500 tracking-tight">{t("อัพโหลดสำเร็จ", "Upload Successful")}</p>
                          <p className="text-sm text-green-500/60 mt-1 font-medium">{uploadedFile?.name || t("คลิกที่นี่หากต้องการเปลี่ยนรูปสลิป", "Click to change slip image")}</p>
                        </div>
                      ) : (
                        <>
                          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent opacity-0 group-hover/upload:opacity-100 transition-opacity"></div>
                          <div className="relative z-10">
                            <div className="h-16 w-16 rounded-3xl bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover/upload:scale-110 group-hover/upload:bg-primary/20 transition-all duration-500">
                              {uploading ? (
                                <CheckCircle2 className="h-8 w-8 text-primary animate-pulse" />
                              ) : (
                                <Upload className="h-8 w-8 text-primary" />
                              )}
                            </div>
                            <p className="text-lg font-bold mb-1 tracking-tight">
                              {uploading ? t("กำลังอัพโหลดสลิป...", "Uploading slip...") : t("อัพโหลดสลิปการโอนเงิน", "Upload payment slip")}
                            </p>
                            <p className="text-xs text-muted-foreground font-medium px-4">
                              {t("กรุณาอัพโหลดหลักฐานการโอนเงินเพื่อดำเนินการตรวจสอบ", "Please upload transfer proof for verification")}
                            </p>
                          </div>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              </CardContent>

              <CardFooter className="flex gap-4 p-6 md:p-8 bg-muted/30 border-t border-border/50">
                <Button variant="outline" className="flex-1 h-12 rounded-xl font-bold border-white/10 hover:bg-white/5 active:scale-95 transition-all text-muted-foreground" onClick={() => setShowPayment(false)}>
                  {t("ยกเลิก", "Cancel")}
                </Button>
                <Button
                  variant="glow"
                  className="flex-[1.5] h-12 rounded-xl font-black text-base shadow-xl shadow-primary/20 active:scale-95 transition-all group/btn"
                  onClick={handleConfirmPayment}
                  disabled={uploading}
                >
                  <span className="mr-2">{t("ยืนยันการชำระเงิน", "Confirm Payment")}</span>
                  <CheckCircle2 className="h-5 w-5 group-hover/btn:scale-110 transition-transform" />
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {paymentSubmitted && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <Card variant="glow" className="w-full max-w-lg">
            <CardHeader className="text-center">
              <div className="mx-auto p-4 rounded-full bg-green-500/20 text-green-500 w-fit mb-4">
                <CheckCircle2 className="h-12 w-12" />
              </div>
              <CardTitle className="text-2xl">{t("แจ้งโอนสำเร็จ!", "Payment Submitted!")}</CardTitle>
              <CardDescription>
                {t("ทีมงานจะตรวจสอบและอนุมัติคำสั่งซื้อให้คุณ", "Our team will review and approve your order")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-muted/50 rounded-xl p-4 space-y-3 text-sm">
                <p className="text-muted-foreground">
                  {t("คำสั่งซื้อของคุณถูกส่งให้ทีมงานตรวจสอบแล้ว", "Your order has been submitted for review.")}
                </p>
                {orderIds.length > 0 && (
                  <div className="space-y-2">
                    <p className="font-semibold">{t("เลขที่คำสั่งซื้อ", "Order IDs")}</p>
                    <div className="space-y-1">
                      {orderIds.map((id) => (
                        <code key={id} className="block bg-background px-3 py-2 rounded-lg text-xs font-mono">
                          {id}
                        </code>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter>
              <Button
                variant="glow"
                className="w-full"
                onClick={() => {
                  setPaymentSubmitted(false);
                  setOrderIds([]);
                  setShowPayment(false);
                }}
              >
                {t("เสร็จสิ้น", "Done")}
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AIProgram;
